import React, { useEffect, useRef, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MobileQuantityStepperProps {
    value: number;
    onValueChange: (value: number) => void;
    /** Backend accepts up to 3 decimal places on quantity. */
    maxDecimals?: number;
    disabled?: boolean;
    'aria-label'?: string;
}

const QUANTITY_RE = /^\d*(?:\.\d{0,3})?$/;

const format = (value: number): string =>
    Number.isInteger(value) ? String(value) : String(Number(value.toFixed(3)));

/**
 * Quantity control for order lines.
 *
 * Collapses to a single "+" button at zero and expands to a full stepper once the
 * line is in the order — the pattern Indian ordering apps use, and it keeps a list
 * of 50 products from reading as 50 identical widgets.
 *
 * The number itself is tappable for direct entry, so a manager can type "250"
 * instead of pressing "+" 250 times.
 */
export function MobileQuantityStepper({
    value,
    onValueChange,
    maxDecimals = 3,
    disabled = false,
    'aria-label': ariaLabel,
}: MobileQuantityStepperProps) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (editing) {
            inputRef.current?.focus();
            inputRef.current?.select();
        }
    }, [editing]);

    const commit = () => {
        const next = Number(draft);
        onValueChange(draft === '' || !Number.isFinite(next) || next < 0 ? 0 : next);
        setEditing(false);
    };

    const handleDraftChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const next = e.target.value;
        if (next === '' || QUANTITY_RE.test(next)) {
            setDraft(next);
        }
    };

    if (value <= 0) {
        return (
            <button
                type="button"
                disabled={disabled}
                aria-label={ariaLabel ? `Add ${ariaLabel}` : 'Add'}
                onClick={() => onValueChange(1)}
                className={cn(
                    'flex items-center justify-center w-11 h-11 rounded-xl flex-shrink-0',
                    'bg-primary/10 text-primary active:bg-primary/20 transition-colors',
                    'disabled:opacity-40 disabled:pointer-events-none'
                )}
            >
                <Plus className="h-5 w-5" strokeWidth={2.5} />
            </button>
        );
    }

    return (
        <div
            className={cn(
                'flex items-center rounded-xl bg-primary/10 flex-shrink-0 h-11',
                disabled && 'opacity-40 pointer-events-none'
            )}
        >
            <button
                type="button"
                aria-label={ariaLabel ? `Decrease ${ariaLabel}` : 'Decrease'}
                onClick={() => onValueChange(Math.max(0, Number((value - 1).toFixed(maxDecimals))))}
                className="flex items-center justify-center w-10 h-11 rounded-l-xl text-primary active:bg-primary/20 transition-colors"
            >
                <Minus className="h-4 w-4" strokeWidth={2.5} />
            </button>

            {editing ? (
                <input
                    ref={inputRef}
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    value={draft}
                    onChange={handleDraftChange}
                    onBlur={commit}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') commit();
                        if (e.key === 'Escape') setEditing(false);
                    }}
                    aria-label={ariaLabel ? `${ariaLabel} quantity` : 'Quantity'}
                    className="w-14 h-11 bg-transparent border-0 outline-none text-center text-base font-semibold text-primary"
                />
            ) : (
                <button
                    type="button"
                    onClick={() => {
                        setDraft(format(value));
                        setEditing(true);
                    }}
                    aria-label={ariaLabel ? `${ariaLabel} quantity, ${value}` : `Quantity ${value}`}
                    className="w-14 h-11 text-base font-semibold text-primary tabular-nums"
                >
                    {format(value)}
                </button>
            )}

            <button
                type="button"
                aria-label={ariaLabel ? `Increase ${ariaLabel}` : 'Increase'}
                onClick={() => onValueChange(Number((value + 1).toFixed(maxDecimals)))}
                className="flex items-center justify-center w-10 h-11 rounded-r-xl text-primary active:bg-primary/20 transition-colors"
            >
                <Plus className="h-4 w-4" strokeWidth={2.5} />
            </button>
        </div>
    );
}
